import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { Params } from '@angular/router';


import { ChartInfo } from '../../../models/common/chartInfo';
import { GridColumn, GridColumnTypeEnum } from '../../../models/common/grid-column.models';
import { CompanyDashboardSchema } from '../../../models/company/company-dashboard.models';
import { CompanyProduct, CompanyProductsSchema } from '../../../models/company/company-products-schema.model';
import { CompanyService } from '../../../services/company.service';
import { SidenavService } from '../../../shared/services/sidenav.service';
import { AppService } from '../../../app.service';
import { PhotographerBase } from '../../../models/common/photographer-base.model';
import { Photographer } from '../../../models/common/photographer.models';
import { SidebarTypeEnum } from '../../../models/common/sidebar-type.enum';
import { UpdatePhotographer } from '../../../models/company/update-photographer.models';
import { UtilityFunctions } from '../../../shared/utilities/functions.models';
import { PhotographerStatusEnum } from '../../../models/common/photographer-status.enum';
import { GraphQLService } from '../../../services/graph-ql.service';
import { GetQuery } from '../../../models/shared/enums/get-query.enum';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';

@Component({
  standalone: false,
  selector: 'bt-company-dashboard',
  templateUrl: './company-dashboard.component.html',
  styleUrls: ['./company-dashboard.component.scss']
})

export class CompanyDashboardComponent implements OnInit {

  readonly #appService = inject(AppService);
  readonly #companyService = inject(CompanyService);
  readonly #sidenavService = inject(SidenavService);
  readonly #graphQLService = inject(GraphQLService);
  readonly #destroyRef = inject(DestroyRef);

  dashboardSchema: CompanyDashboardSchema;
  expensesChart: ChartInfo;
  databaseStatusChart: ChartInfo;
  noImgChart: ChartInfo;
  productsGridColumns: Array<GridColumn>;
  photographersColumns: Array<GridColumn>;
  photographersOnRequest: { data: Array<PhotographerBase>, dataToView?: Array<PhotographerBase>, page?: number, size: number } = {
    size: 5,
    data: []
  };
  tmpPhotog: Photographer;
  gridColumnTypeEnum = GridColumnTypeEnum;
  products: { data: Array<CompanyProduct>, pageNumber?: number, pageSize: number, loaderId: string, total: number } = {
    total: 0,
    pageSize: 5,
    loaderId: UtilityFunctions.newGuid(),
    data: []
  };
  loaderId = UtilityFunctions.newGuid();
  currentSidebar: SidebarTypeEnum;
  sidebarType = SidebarTypeEnum;

  constructor(
  ) {
    this.setProductsGridColumns();
    this.setPhotographersColumns();
  }

  public ngOnInit() {
    this.getCompanyDashboardSchema();
    this.getCompanyProducts(0);
  }

  public onItemAction(item: PhotographerBase, sidebarType: SidebarTypeEnum) {
    item.showMenu = false;
    this.currentSidebar = sidebarType;
    this.tmpPhotog = UtilityFunctions.DeepClone(new Photographer({
      active: true,
      automaticRenewal: false,
      brands: [],
      companyId: this.#appService.companyId,
      firstName: item.firstName,
      fullName: item.fullName,
      lastName: item.lastName,
      personId: item.personId,
    }));
  }

  public onInfo(item: CompanyProduct) {
    const params: Params = { orderId: item.orderId, orderNumber: item.orderNumber, modelId: item.modelId };
    this.#sidenavService.newItemUrlPart('order-list', params);
  }

  public savedInfo() {
    this.tmpPhotog = null;
    this.currentSidebar = null;
    this.getPhotographersRequestedStatus();
  }

  public createdCustomOrder() {
    this.tmpPhotog = null;
    this.getPhotographersRequestedStatus();
    this.currentSidebar = null;
  }

  public deletePhotographer() {
    const updatePhotographer = new UpdatePhotographer({
      PhotographerId: this.tmpPhotog.personId,
      DiscountPercentage: undefined,
      DiscountThreshold: undefined,
      EndDate: undefined,
      StartDate: undefined,
      MaxItems: undefined,
      ShotCost: undefined,
      StatusId: PhotographerStatusEnum.Deleted,
      AutomaticRenewal: false,
      BrandIdCategoryId: []
    });
    this.#companyService.updatePhotographer(updatePhotographer, this.loaderId).subscribe(() => {
      this.getPhotographersRequestedStatus();
    });
  }

  public setPhotoToView(page: number) {
    this.photographersOnRequest.page = page;
    const start = this.photographersOnRequest.page * this.photographersOnRequest.size;
    const dataToView = this.photographersOnRequest.data.slice(start, start + this.photographersOnRequest.size);
    !dataToView.length && this.photographersOnRequest.page !== 0 ?
      this.setPhotoToView(this.photographersOnRequest.page - 1) : this.photographersOnRequest.dataToView = dataToView;
  }

  getCompanyProducts(page: number) {
    this.products.pageNumber = page;

    const filters = {
      paginationPage: page,
      paginationSize: this.products.pageSize,
      companyId: this.#appService.companyId
    }

    this.#graphQLService.getData<CompanyProductsSchema>(GetQuery.GET_PRODUCTS, filters)
      .pipe(takeUntilDestroyed(this.#destroyRef), map(res => res['data']['products']['nodes'][0]))
      .subscribe((data: CompanyProductsSchema) => {
        this.products.data = data.products;
        this.products.total = data.total;
      });
  }

  private getPhotographersRequestedStatus() {
    this.#graphQLService.getData(GetQuery.GET_PHOTOGRAPHER_STATUS_REQUESTED, { companyId: this.#appService.companyId })
      .pipe(takeUntilDestroyed(this.#destroyRef), map((res: any) => res['data']['photographersStatusRequested']['nodes']))
      .subscribe((data: Array<PhotographerBase>) => {
        this.photographersOnRequest.data = data.map(el => ({firstName: el.firstName, lastName: el.lastName, personId: el.personId, fullName: `${el.firstName?.toLowerCase()} ${el.lastName?.toLowerCase()}`}));
        this.setPhotoToView(0);
      });
  }

  private getCompanyDashboardSchema() {
    this.#graphQLService.getData<CompanyDashboardSchema>(GetQuery.GET_COMPANY_DASHBOARD, { companyId: this.#appService.companyId })
      .pipe(takeUntilDestroyed(this.#destroyRef), map(res => res['data']['companyDashboard']['nodes'][0]))
      .subscribe((data: CompanyDashboardSchema) => {
        this.dashboardSchema = UtilityFunctions.DeepClone(data);
        this.setCharts();
        this.getPhotographersRequestedStatus();
      });
  }

  private setCharts() {
    this.expensesChart = new ChartInfo({
      type: 'line',
      data: {
        labels: ['1', '2', '3', '1', '2', '3', '1', '2', '3', '1', '2', '3', '1', '2', '3'],
        datasets: [{
          data: [12, 13, 40, 45, 46, 40, 76, 34, 40, 44, 31, 40, 50, 13, 40],
          backgroundColor: 'rgba(69, 90, 100, .5)',
          borderColor: 'rgba(69, 90, 100, 0.2)',
          borderWidth: 1,
          fill: true,
          tension: 0.4,
          pointBackgroundColor: 'rgba(69, 90, 100, 0.2)',
          pointBorderColor: 'rgba(69, 90, 100, 0.2)',
        }],
      },
      options: { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } } },
    });

    this.databaseStatusChart = new ChartInfo({
      type: 'pie',
      data: {
        labels: ['With images', 'Without images'],
        datasets: [{
          data: [this.dashboardSchema.databaseStatusData.withImg, this.dashboardSchema.databaseStatusData.withoutImg],
          backgroundColor: ['rgba(69, 90, 100, .5)', 'rgb(229, 229, 229)']

        }]
      },
      options: { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: true, position: 'right' } } },
    });

    this.noImgChart = new ChartInfo({
      type: 'pie',
      data: {
        labels: ['Active', 'On hold'],
        datasets: [{
          data: [this.dashboardSchema.noImageItemsData.active, this.dashboardSchema.noImageItemsData.onHold],
          backgroundColor: ['rgba(69, 90, 100, .5)', 'rgb(229, 229, 229)']
        }]
      },
      options: { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: true, position: 'right' } } },
    });
  }

  private setProductsGridColumns() {
    this.productsGridColumns = [
      { title: '', field: '', type: GridColumnTypeEnum.icon },
      { title: 'Item Name', field: 'styleFullName', type: GridColumnTypeEnum.default },
      { title: 'Order #', field: 'orderNumber', type: GridColumnTypeEnum.default },
      { title: 'Photographer', field: 'photographerFullName', type: GridColumnTypeEnum.default },
      { title: 'Due Date', field: 'dueDate', type: GridColumnTypeEnum.date }
    ];
  }

  private setPhotographersColumns() {
    this.photographersColumns = [
      { title: '', field: '', type: GridColumnTypeEnum.icon, width: 16 },
      { title: 'Last Name', field: 'lastName', type: GridColumnTypeEnum.default },
      { title: 'First Name', field: 'firstName', type: GridColumnTypeEnum.default }
    ];
  }
}
